import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    appConfigFailed,
    appConfigLoaded,
    appConfigLoadedFromStorage,
    loadAppConfig,
} from '../actions/app-config.actions';
import { AppConfigState, AppConfigValuesState, initialAppConfigState } from '../app-config.state';

/**
 * Marks the slice as loading while the backend GET is in flight. Runs on both the initial boot
 * load and a refresh after a location-tracker save, so a refresh in flight is visible the same
 * way a boot load is.
 */
export function setAppConfigLoadingReducer(state: Readonly<AppConfigState>): Readonly<AppConfigState> {
    return { ...state, status: LoadStatus.Loading };
}

/** Populates the slice from a successful backend load and marks it loaded. Triggers a persist. */
export function setAppConfigValuesReducer(
    state: Readonly<AppConfigState>,
    { values }: { values: AppConfigValuesState },
): Readonly<AppConfigState> {
    return { ...state, values, status: LoadStatus.Loaded };
}

/**
 * Populates the slice from the persisted cache. Deliberately leaves `status` untouched: `status`
 * tracks the backend fetch only (see `AppConfigState`'s doc comment), so a cache hit must not
 * make boot succeed offline the way it would if this flipped to `Loaded`.
 */
export function setAppConfigValuesFromStorageReducer(
    state: Readonly<AppConfigState>,
    { values }: { values: AppConfigValuesState },
): Readonly<AppConfigState> {
    return { ...state, values };
}

/**
 * Marks the load as failed. `values` is LEFT IN PLACE so cached values survive a failed refresh
 * (e.g. the app-config reload after a location-tracker save failing) rather than being wiped out.
 */
export function setAppConfigErrorReducer(state: Readonly<AppConfigState>): Readonly<AppConfigState> {
    return { ...state, status: LoadStatus.Error };
}

const createAppConfigReducer = createReducer(
    initialAppConfigState,
    on(loadAppConfig, setAppConfigLoadingReducer),
    on(appConfigLoaded, setAppConfigValuesReducer),
    on(appConfigLoadedFromStorage, setAppConfigValuesFromStorageReducer),
    on(appConfigFailed, setAppConfigErrorReducer),
);

export function appConfigReducer(
    state: Readonly<AppConfigState> | undefined,
    action: Action,
): Readonly<AppConfigState> {
    return createAppConfigReducer(state, action);
}
